/**
 * DateRangeContext
 * Global date range state management for filtering data across all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { startOfToday, endOfToday, subDays, startOfWeek, endOfWeek,
         startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
         startOfYear, endOfYear, format } from 'date-fns'

// Define preset date ranges
export const DATE_PRESETS = {
  today: {
    label: 'Today',
    getValue: () => ({
      startDate: startOfToday(),
      endDate: endOfToday(),
      preset: 'today'
    })
  },
  last7Days: {
    label: 'Last 7 Days',
    getValue: () => ({
      startDate: subDays(startOfToday(), 6),
      endDate: endOfToday(),
      preset: 'last7Days'
    })
  },
  last30Days: {
    label: 'Last 30 Days',
    getValue: () => ({
      startDate: subDays(startOfToday(), 29),
      endDate: endOfToday(),
      preset: 'last30Days'
    })
  },
  thisWeek: {
    label: 'This Week',
    getValue: () => ({
      startDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
      endDate: endOfWeek(new Date(), { weekStartsOn: 0 }),
      preset: 'thisWeek'
    })
  },
  thisMonth: {
    label: 'This Month',
    getValue: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      preset: 'thisMonth'
    })
  },
  thisQuarter: {
    label: 'This Quarter',
    getValue: () => ({
      startDate: startOfQuarter(new Date()),
      endDate: endOfQuarter(new Date()),
      preset: 'thisQuarter'
    })
  },
  thisYear: {
    label: 'This Year',
    getValue: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date()),
      preset: 'thisYear'
    })
  },
  allTime: {
    label: 'All Time',
    getValue: () => ({
      startDate: null,
      endDate: null,
      preset: 'allTime'
    })
  }
}

const DateRangeContext = createContext()

// Local storage key for persistence
const STORAGE_KEY = 'cortex_date_range'

export const DateRangeProvider = ({ children }) => {
  // Initialize from localStorage or default to Last 30 Days
  const [dateRange, setDateRange] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Restore dates from ISO strings
        return {
          ...parsed,
          startDate: parsed.startDate ? new Date(parsed.startDate) : null,
          endDate: parsed.endDate ? new Date(parsed.endDate) : null
        }
      } catch (e) {
        console.error('Failed to parse stored date range:', e)
      }
    }
    // Default to All Time
    return DATE_PRESETS.allTime.getValue()
  })

  const [isCustomRange, setIsCustomRange] = useState(false)

  // Persist to localStorage whenever dateRange changes
  useEffect(() => {
    const toStore = {
      ...dateRange,
      startDate: dateRange.startDate ? dateRange.startDate.toISOString() : null,
      endDate: dateRange.endDate ? dateRange.endDate.toISOString() : null
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
  }, [dateRange])

  // Set a preset date range
  const setPresetRange = useCallback((presetKey) => {
    if (DATE_PRESETS[presetKey]) {
      const range = DATE_PRESETS[presetKey].getValue()
      setDateRange(range)
      setIsCustomRange(false)
    }
  }, [])

  // Set a custom date range
  const setCustomRange = useCallback((startDate, endDate) => {
    setDateRange({
      startDate,
      endDate,
      preset: null
    })
    setIsCustomRange(true)
  }, [])

  // Format date range for display
  const getFormattedRange = useCallback(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return 'All Time'
    }

    if (dateRange.preset && DATE_PRESETS[dateRange.preset]) {
      return DATE_PRESETS[dateRange.preset].label
    }

    return `${format(dateRange.startDate, 'MMM d, yyyy')} - ${format(dateRange.endDate, 'MMM d, yyyy')}`
  }, [dateRange.startDate, dateRange.endDate, dateRange.preset])

  // Get date range as ISO strings for API calls
  const getApiDateRange = useCallback(() => {
    return {
      start_date: dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : null,
      end_date: dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : null
    }
  }, [dateRange.startDate, dateRange.endDate])

  // Clear date range (set to All Time)
  const clearRange = useCallback(() => {
    setPresetRange('allTime')
  }, [setPresetRange])

  const value = {
    dateRange,
    isCustomRange,
    setPresetRange,
    setCustomRange,
    getFormattedRange,
    getApiDateRange,
    clearRange,
    presets: DATE_PRESETS
  }

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  )
}

export const useDateRange = () => {
  const context = useContext(DateRangeContext)
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider')
  }
  return context
}